import { useState } from 'react';
import { ItemArt } from '../avatar/itemArt';
import { AvatarView } from '../avatar/AvatarView';
import { Bubble } from '../components/Bubble';
import { Sheet } from '../components/Sheet';
import { type CatalogItem, type Equipped, type Player, SLOTS, type Slot, slotLabel } from '../models/types';
import { catalog } from '../state/catalog';
import { usePlayer } from '../state/player';
import { useShell } from '../state/shell';
import { colors, rarityColors, withAlpha } from '../theme';

/** Equip and remove owned items slot by slot, with a live preview. Every tap is shown at once and saved. */
export function WardrobeSheet({ initialSlot = 'head', onClose }: { initialSlot?: Slot; onClose: () => void }) {
  const player = usePlayer((s) => s.player);
  const showToast = useShell((s) => s.showToast);
  const [selected, setSelected] = useState<Slot>(initialSlot);

  const save = async (equipped: Equipped) => {
    try {
      await usePlayer.getState().setEquipped(equipped);
    } catch {
      showToast("Couldn't save your outfit. Try again.");
    }
  };

  const toggle = (itemId: string) => {
    if (!player) return;
    const next: Equipped = { ...player.avatar.equipped };
    if (next[selected] === itemId) delete next[selected];
    else next[selected] = itemId;
    void save(next);
  };

  return (
    <Sheet tall label="Your wardrobe" onClose={onClose}>
      {!player ? (
        <div className="center-fill"><span className="spinner dark" role="progressbar" aria-label="Loading" /></div>
      ) : (
        <>
          <h2 className="t-title-l t-center" style={{ fontWeight: 900 }}>Your wardrobe</h2>
          <div style={{ margin: '8px 0', flex: 'none' }} data-testid="wardrobe-preview">
            <AvatarView avatar={player.avatar} size={160} onSlotTap={setSelected} />
          </div>
          <div style={{ display: 'flex', gap: 8, overflowX: 'auto', padding: '4px 0 8px', flex: 'none' }} role="tablist" aria-label="Slots">
            {SLOTS.map((slot) => (
              <button
                key={slot}
                type="button"
                role="tab"
                aria-selected={slot === selected}
                className={`chip${slot === selected ? ' selected' : ''}`}
                data-testid={`wardrobe-slot-${slot}`}
                style={{ flex: 'none' }}
                onClick={() => setSelected(slot)}
              >
                {player.avatar.equipped[slot] ? '✨ ' : ''}
                {slotLabel(slot)}
              </button>
            ))}
          </div>
          <SlotItems slot={selected} player={player} onTap={toggle} />
          <div style={{ padding: '4px 0 16px', flex: 'none' }}>
            <button type="button" className="btn outlined block" disabled={Object.keys(player.avatar.equipped).length === 0} onClick={() => void save({})}>
              Take everything off
            </button>
          </div>
        </>
      )}
    </Sheet>
  );
}

/** Distinct owned items worn in `slot`, newest first. */
function ownedFor(player: Player, slot: Slot): CatalogItem[] {
  const seen = new Set<string>();
  const items: CatalogItem[] = [];
  for (const inst of [...player.inventory].sort((a, b) => b.foundAtTs - a.foundAtTs)) {
    if (seen.has(inst.itemId) || !catalog.contains(inst.itemId)) continue;
    seen.add(inst.itemId);
    const item = catalog.get(inst.itemId);
    if (item.slot === slot) items.push(item);
  }
  return items;
}

function SlotItems({ slot, player, onTap }: { slot: Slot; player: Player; onTap: (id: string) => void }) {
  const items = ownedFor(player, slot);
  if (items.length === 0) {
    return <div className="center-fill t-title-m">Nothing for your {slot} yet. Keep scanning!</div>;
  }
  const wearing = player.avatar.equipped[slot];
  return (
    <div style={{ flex: 1, minHeight: 0, overflowY: 'auto', padding: '8px 0' }}>
      <div className="grid-3">
        {items.map((item) => {
          const on = item.id === wearing;
          return (
            <Bubble
              key={item.id}
              small
              testId={`wardrobe-item-${item.id}`}
              onClick={() => onTap(item.id)}
              color={withAlpha(item.palette, on ? 1 : 0.6)}
              border={`${on ? 4 : 2}px solid ${on ? colors.ink : rarityColors[item.rarity]}`}
              padding={8}
              style={{ textAlign: 'center', minHeight: 110 }}
            >
              <ItemArt item={item} style={{ fontSize: 40, display: 'block' }} />
              <div className="t-small" style={{ fontWeight: 700 }}>{item.name}</div>
              {on && <div className="t-small" style={{ fontWeight: 900 }}>Wearing</div>}
            </Bubble>
          );
        })}
      </div>
    </div>
  );
}
